
import { supabase } from "@/integrations/supabase/client";
import { handleError } from '../errorHandling';
import { hasReachedLocalFreeLimit } from './localStorage';

/**
 * Check if user has reached free entry limit
 */
export async function hasReachedFreeLimit(userId?: string): Promise<boolean> {
  if (!userId) return false;
  
  try {
    const { count, error } = await supabase
      .from('diary_entries')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_premium', false);
      
    if (error) throw error;
    
    return count !== null && count >= 30;
  } catch (error) {
    handleError(error, "Error checking free entry limit");
    return hasReachedLocalFreeLimit(userId);
  }
}

/**
 * Sync local entries to Supabase
 */
export async function syncLocalEntries(userId: string): Promise<void> {
  if (!userId) return;
  
  const localEntries = await import('./localStorage').then(m => m.getLocalUserEntries(userId));
  if (localEntries.length === 0) return;
  
  try {
    for (const entry of localEntries) {
      const { data } = await supabase
        .from('diary_entries')
        .select('id')
        .eq('id', entry.id)
        .single();
        
      if (!data) {
        await import('./diaryOperations').then(m => m.saveEntry(entry));
      }
    }
    
    console.log(`Synced ${localEntries.length} entries from localStorage to Supabase`);
  } catch (error) {
    handleError(error, "Error syncing entries");
  }
}

/**
 * Manually check and refresh premium status for a user
 */
export async function checkAndRefreshPremiumStatus(userId: string): Promise<boolean> {
  if (!userId) return false;
  
  try {
    // Get current premium status directly from the database
    const { data, error } = await supabase
      .from('profiles')
      .select('is_premium')
      .eq('id', userId)
      .single();
      
    if (error) throw error;
    
    const isPremium = data?.is_premium || false;
    
    // Update local storage with the latest status
    localStorage.setItem('premiumSubscription', isPremium ? 'true' : 'false');
    
    return isPremium;
  } catch (error) {
    handleError(error, "Error checking premium status");
    return false;
  }
}
